import React, { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { Button, Col, Row } from "reactstrap";
import {
  Card,
  Switch,
  InputNumber,
  DatePicker,
  Radio,
  Divider,
  Select,
} from "antd";
import { InfoCircleOutlined, DollarOutlined } from "@ant-design/icons";
import FormSelect from "../../form-components/SelectComponent";
import FormInput from "../../form-components/InputComponent";
import FormDatePicker from "../../form-components/DatePicker";
import {
  WorkOrderFormData,
  PaymentConditions,
  RecoveryType,
  WorkType,
} from "../types";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { indianCurrencyFormatter, indianCurrencyParser } from "../../../utils/Utils";

interface SelectOptions {
  label: string;
  value: string;
}

const WorkOrderPaymentConditions: React.FC = (props: any) => {
  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<WorkOrderFormData>();

  const paymentConditionsWatch = watch("paymentConditions");
  const workOrderLinesWatch = watch("workOrderLines") || [];

  // Recovery type options
  const recoveryTypeOptions: SelectOptions[] = [
    { label: "Each RA Bill", value: RecoveryType.EachRABill },
    { label: "Final Bill", value: RecoveryType.FinalBill },
  ];

  // DLP unit options
  const dlpUnitOptions = [
    { label: "Days", value: "days" },
    { label: "Months", value: "months" },
    { label: "Years", value: "years" },
  ];

  // Calculate estimated contract value from work order lines
  const estimatedContractValue = workOrderLinesWatch.reduce(
    (sum, line) => sum + line.quantity * line.rate,
    0
  );

  // Check if all work order lines are OPEN type (no quantity, so contract value = 0)
  const isOpenWorkType =
    workOrderLinesWatch.length > 0 &&
    workOrderLinesWatch.every((line: any) => line.workType === WorkType.Open);

  useEffect(() => {
    // Initialize payment conditions if not set
    if (!paymentConditionsWatch) {
      console.log(
        "Initializing default payment conditions with calculated release dates"
      );
      const defaultRetentionReleaseDate = calculateReleaseDate(1, "years");
      const defaultOnHoldReleaseDate = calculateReleaseDate(1, "years");

      const defaultPaymentConditions: PaymentConditions = {
        mobilizationAdvance: {
          isPercentOfContract: false,
          percentageOfContract: 0,
          advanceValue: 0,
          interestCharged: false,
          interestRate: 0,
          recoveryType: RecoveryType.EachRABill,
          recoveryPercentRate: 0,
        },
        retentionAmount: {
          isPercentOfRABill: false,
          percentageOfRABill: 0,
          retentionValue: 0,
          dlpAmount: 1,
          dlpUnit: "years" as const,
          releaseDate: defaultRetentionReleaseDate,
        },
        onHoldAmount: {
          isPercentOfContract: false,
          percentageOfContract: 0,
          holdAmountValue: 0,
          dlpAmount: 1,
          dlpUnit: "years" as const,
          releaseDate: defaultOnHoldReleaseDate,
        },
        payOnWorkCompletion: false,
      };
      console.log("Setting default conditions with release dates:", {
        retentionReleaseDate: defaultRetentionReleaseDate,
        onHoldReleaseDate: defaultOnHoldReleaseDate,
      });
      setValue("paymentConditions", defaultPaymentConditions);
    }
  }, [paymentConditionsWatch, setValue]);

  // Auto-calculate release dates when DLP values change or on initial load
  useEffect(() => {
    if (paymentConditionsWatch) {
      console.log(
        "Auto-calculation useEffect triggered, checking conditions:",
        {
          retentionAmount: paymentConditionsWatch.retentionAmount,
          onHoldAmount: paymentConditionsWatch.onHoldAmount,
        }
      );

      let needsUpdate = false;
      const updatedConditions = { ...paymentConditionsWatch };

      // Auto-calculate retention release date if DLP is set
      if (paymentConditionsWatch.retentionAmount) {
        const dlpAmount = paymentConditionsWatch.retentionAmount.dlpAmount || 1;
        const dlpUnit =
          paymentConditionsWatch.retentionAmount.dlpUnit || "years";
        const currentReleaseDate =
          paymentConditionsWatch.retentionAmount.releaseDate;
        const calculatedDate = calculateReleaseDate(dlpAmount, dlpUnit);

        // Update if no release date or if calculated date is different
        if (!currentReleaseDate || currentReleaseDate !== calculatedDate) {
          console.log("Auto-calculating retention release date:", {
            dlpAmount,
            dlpUnit,
            calculatedDate,
            currentReleaseDate,
          });
          updatedConditions.retentionAmount = {
            ...updatedConditions.retentionAmount,
            releaseDate: calculatedDate,
          };
          needsUpdate = true;
        }
      }

      // Auto-calculate on hold release date if DLP is set
      if (paymentConditionsWatch.onHoldAmount) {
        const dlpAmount = paymentConditionsWatch.onHoldAmount.dlpAmount || 1;
        const dlpUnit = paymentConditionsWatch.onHoldAmount.dlpUnit || "years";
        const currentReleaseDate =
          paymentConditionsWatch.onHoldAmount.releaseDate;
        const calculatedDate = calculateReleaseDate(dlpAmount, dlpUnit);

        // Update if no release date or if calculated date is different
        if (!currentReleaseDate || currentReleaseDate !== calculatedDate) {
          console.log("Auto-calculating on hold release date:", {
            dlpAmount,
            dlpUnit,
            calculatedDate,
            currentReleaseDate,
          });
          updatedConditions.onHoldAmount = {
            ...updatedConditions.onHoldAmount,
            releaseDate: calculatedDate,
          };
          needsUpdate = true;
        }
      }

      // Update form values if changes were made
      if (needsUpdate) {
        console.log(
          "Updating payment conditions with calculated release dates",
          updatedConditions
        );
        setValue("paymentConditions", updatedConditions, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    }
  }, [
    paymentConditionsWatch?.retentionAmount?.dlpAmount,
    paymentConditionsWatch?.retentionAmount?.dlpUnit,
    paymentConditionsWatch?.onHoldAmount?.dlpAmount,
    paymentConditionsWatch?.onHoldAmount?.dlpUnit,
    paymentConditionsWatch?.retentionAmount?.releaseDate,
    paymentConditionsWatch?.onHoldAmount?.releaseDate,
    paymentConditionsWatch,
    setValue,
  ]);

  const validateForm = (data: any) => {
    const conditions = data.paymentConditions;

    // Payment conditions are now optional - no validation required
    // Users can leave all fields empty or partially filled

    // Only validate values that are actually provided
    if (conditions) {
      // Mobilization Advance - only validate if values are provided
      if (
        conditions.mobilizationAdvance?.isPercentOfContract &&
        conditions.mobilizationAdvance?.percentageOfContract
      ) {
        if (
          conditions.mobilizationAdvance.percentageOfContract <= 0 ||
          conditions.mobilizationAdvance.percentageOfContract > 100
        ) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: "Mobilization advance percentage must be between 1 and 100",
          });
          return false;
        }
      }

      // Interest rate - only validate if interest is charged and rate is provided
      if (
        conditions.mobilizationAdvance?.interestCharged &&
        conditions.mobilizationAdvance?.interestRate &&
        conditions.mobilizationAdvance.interestRate <= 0
      ) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Interest rate must be greater than 0 when interest is charged",
        });
        return false;
      }

      // Retention Amount - only validate if values are provided
      if (
        conditions.retentionAmount?.isPercentOfRABill &&
        conditions.retentionAmount?.percentageOfRABill
      ) {
        if (
          conditions.retentionAmount.percentageOfRABill <= 0 ||
          conditions.retentionAmount.percentageOfRABill > 50
        ) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: "Retention percentage must be between 1 and 50",
          });
          return false;
        }
      }

      // On Hold Amount - only validate if values are provided
      if (
        conditions.onHoldAmount?.isPercentOfContract &&
        conditions.onHoldAmount?.percentageOfContract
      ) {
        if (
          conditions.onHoldAmount.percentageOfContract <= 0 ||
          conditions.onHoldAmount.percentageOfContract > 100
        ) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: "On hold percentage must be between 1 and 100",
          });
          return false;
        }
      }
    }

    return true;
  };

  const submitForm = async (data: WorkOrderFormData) => {
    console.log("Payment Conditions data:", data);
    if (!validateForm(data)) {
      console.log("Validation failed");
      return;
    }

    const payload = {
      ...data,
      step: "paymentConditions",
    };

    console.log("Payment Conditions Payload:", payload);
    props.next(payload);
  };

  const handlePrevious = () => {
    if (props.prev) {
      props.prev();
    }
  };

  const updatePaymentCondition = (section: any, field: string, value: any) => {
    const currentConditions: any = getValues("paymentConditions") || {};
    let updatedConditions: any;

    if (section === "" || !section) {
      // For top-level fields like payOnWorkCompletion
      updatedConditions = {
        ...currentConditions,
        [field]: value,
      };
    } else {
      // For nested fields within sections
      updatedConditions = {
        ...currentConditions,
        [section]: {
          ...currentConditions[section],
          [field]: value,
        },
      };
    }
    setValue("paymentConditions", updatedConditions);
  };

  const calculateMobilizationAmount = () => {
    if (!paymentConditionsWatch?.mobilizationAdvance) return 0;
    const { isPercentOfContract, percentageOfContract, advanceValue } =
      paymentConditionsWatch.mobilizationAdvance;

    if (isPercentOfContract && percentageOfContract) {
      return (estimatedContractValue * percentageOfContract) / 100;
    }
    return advanceValue || 0;
  };

  const calculateRetentionAmount = () => {
    if (!paymentConditionsWatch?.retentionAmount) return 0;
    const { isPercentOfRABill, percentageOfRABill, retentionValue } =
      paymentConditionsWatch.retentionAmount;

    if (isPercentOfRABill && percentageOfRABill) {
      return (estimatedContractValue * percentageOfRABill) / 100;
    }
    return retentionValue || 0;
  };

  const calculateOnHoldAmount = () => {
    if (!paymentConditionsWatch?.onHoldAmount) return 0;
    const { isPercentOfContract, percentageOfContract, holdAmountValue } =
      paymentConditionsWatch.onHoldAmount;

    if (isPercentOfContract && percentageOfContract) {
      return (estimatedContractValue * percentageOfContract) / 100;
    }
    return holdAmountValue || 0;
  };

  // Calculate total of all payment conditions
  const calculateTotalPaymentConditions = () => {
    return (
      calculateMobilizationAmount() +
      calculateRetentionAmount() +
      calculateOnHoldAmount()
    );
  };

  // Calculate remaining available amount for payment conditions
  const calculateRemainingAmount = (excludeSection?: string) => {
    let total = 0;
    if (excludeSection !== "mobilization") {
      total += calculateMobilizationAmount();
    }
    if (excludeSection !== "retention") {
      total += calculateRetentionAmount();
    }
    if (excludeSection !== "onHold") {
      total += calculateOnHoldAmount();
    }
    return estimatedContractValue - total;
  };

  // Calculate release date based on DLP
  const calculateReleaseDate = (
    dlpAmount: number,
    dlpUnit: "days" | "months" | "years"
  ) => {
    const today = dayjs();
    switch (dlpUnit) {
      case "days":
        return today.add(dlpAmount, "day").format("YYYY-MM-DD");
      case "months":
        return today.add(dlpAmount, "month").format("YYYY-MM-DD");
      case "years":
        return today.add(dlpAmount, "year").format("YYYY-MM-DD");
      default:
        return today.add(dlpAmount, "year").format("YYYY-MM-DD");
    }
  };

  // Update release date when DLP changes for retention
  const updateRetentionDLP = (field: string, value: any) => {
    updatePaymentCondition("retentionAmount", field, value);

    // Auto-calculate release date if DLP is set
    const currentConditions = getValues("paymentConditions") || {};
    const retentionAmount = currentConditions.retentionAmount || {};

    const dlpAmount =
      field === "dlpAmount" ? value : retentionAmount.dlpAmount || 1;
    const dlpUnit =
      field === "dlpUnit" ? value : retentionAmount.dlpUnit || "years";

    if (dlpAmount && dlpUnit) {
      const calculatedDate = calculateReleaseDate(dlpAmount, dlpUnit);
      updatePaymentCondition("retentionAmount", "releaseDate", calculatedDate);
    }
  };

  // Update release date when DLP changes for on hold
  const updateOnHoldDLP = (field: string, value: any) => {
    updatePaymentCondition("onHoldAmount", field, value);

    // Auto-calculate release date if DLP is set
    const currentConditions = getValues("paymentConditions") || {};
    const onHoldAmount = currentConditions.onHoldAmount || {};

    const dlpAmount =
      field === "dlpAmount" ? value : onHoldAmount.dlpAmount || 1;
    const dlpUnit =
      field === "dlpUnit" ? value : onHoldAmount.dlpUnit || "years";

    if (dlpAmount && dlpUnit) {
      const calculatedDate = calculateReleaseDate(dlpAmount, dlpUnit);
      updatePaymentCondition("onHoldAmount", "releaseDate", calculatedDate);
    }
  };

  return (
    <>
      <form className="content clearfix" onSubmit={handleSubmit(submitForm)}>
        <Card className="mb-4">
          {/* <p className="text-muted mb-4">
            Configure payment terms and conditions for this work order. 
            These conditions will determine how payments are processed throughout the project lifecycle.
          </p> */}
          {!isOpenWorkType && (
            <div className="alert alert-info mb-4">
              <InfoCircleOutlined className="me-2" />
              <strong>Estimated Contract Value:</strong> ₹
              {estimatedContractValue.toLocaleString("en-IN")}
              <br />
              <small>
                Based on {workOrderLinesWatch.length} work order line(s)
              </small>
              {/* <br />
            <small><strong>Note:</strong> All payment conditions are optional. You can fill only the ones that apply to your work order.</small> */}
            </div>
          )}

          {/* 1. Mobilization Advance */}
          <Card size="small" title="1. Mobilization Advance" className="mb-4">
            <Row className="gy-3">
              {/* For OPEN work type, only Fixed Amount is allowed */}
              {isOpenWorkType ? (
                <Col lg="12">
                  <div className="form-group">
                    <label className="form-label">Amount Type</label>
                    <div className="text-muted small">
                      Fixed Amount (Open Work Type)
                    </div>
                  </div>
                </Col>
              ) : (
                <Col lg="12">
                  <div className="form-group">
                    <label className="form-label">Amount Type</label>
                    <Radio.Group
                      value={
                        paymentConditionsWatch?.mobilizationAdvance
                          ?.isPercentOfContract
                      }
                      onChange={(e) =>
                        updatePaymentCondition(
                          "mobilizationAdvance",
                          "isPercentOfContract",
                          e.target.value
                        )
                      }
                    >
                      <Radio value={true}>Percentage of Contract Value</Radio>
                      <Radio value={false}>Fixed Amount</Radio>
                    </Radio.Group>
                  </div>
                </Col>
              )}

              {/* For OPEN work type, always show Fixed Amount field without contract value linking */}
              {isOpenWorkType ? (
                <Col lg="6">
                  <div className="form-group">
                    <label className="form-label">Advance Value (₹)</label>
                    <InputNumber
                      min={0}
                      value={
                        paymentConditionsWatch?.mobilizationAdvance
                          ?.advanceValue
                      }
                      onChange={(value) => {
                        updatePaymentCondition(
                          "mobilizationAdvance",
                          "advanceValue",
                          value
                        );
                        // Ensure isPercentOfContract is set to false for OPEN work type
                        updatePaymentCondition(
                          "mobilizationAdvance",
                          "isPercentOfContract",
                          false
                        );
                      }}
                      style={{ width: "100%" }}
                      placeholder="Enter advance amount"
                      formatter={indianCurrencyFormatter}
                      parser={indianCurrencyParser}
                    />
                  </div>
                </Col>
              ) : paymentConditionsWatch?.mobilizationAdvance
                  ?.isPercentOfContract ? (
                <Col lg="6">
                  <div className="form-group">
                    <label className="form-label">
                      Percentage of Contract Value (%)
                    </label>
                    <InputNumber
                      min={0}
                      max={100}
                      value={
                        paymentConditionsWatch?.mobilizationAdvance
                          ?.percentageOfContract
                      }
                      onChange={(value) => {
                        const calculatedAmount = value
                          ? (estimatedContractValue * value) / 100
                          : 0;
                        const remainingAmount =
                          calculateRemainingAmount("mobilization");

                        if (calculatedAmount > remainingAmount) {
                          Swal.fire({
                            icon: "warning",
                            title: "Insufficient Balance",
                            text: `Total payment conditions cannot exceed contract value. Available: ₹${remainingAmount.toLocaleString("en-IN")}`,
                          });
                          return;
                        }
                        updatePaymentCondition(
                          "mobilizationAdvance",
                          "percentageOfContract",
                          value
                        );
                      }}
                      style={{ width: "100%" }}
                      placeholder="Enter percentage"
                    />
                    <small className="text-muted">
                      Available: ₹
                      {calculateRemainingAmount(
                        "mobilization"
                      ).toLocaleString("en-IN")}
                    </small>
                  </div>
                </Col>
              ) : (
                <Col lg="6">
                  <div className="form-group">
                    <label className="form-label">Advance Value (₹)</label>
                    <InputNumber
                      min={0}
                      max={calculateRemainingAmount("mobilization")}
                      value={
                        paymentConditionsWatch?.mobilizationAdvance
                          ?.advanceValue
                      }
                      onChange={(value) => {
                        const remainingAmount =
                          calculateRemainingAmount("mobilization");
                        if (value && value > remainingAmount) {
                          Swal.fire({
                            icon: "warning",
                            title: "Insufficient Balance",
                            text: `Total payment conditions cannot exceed contract value. Available: ₹${remainingAmount.toLocaleString("en-IN")}`,
                          });
                          return;
                        }
                        updatePaymentCondition(
                          "mobilizationAdvance",
                          "advanceValue",
                          value
                        );
                      }}
                      style={{ width: "100%" }}
                      placeholder="Enter advance amount"
                      formatter={indianCurrencyFormatter}
                      parser={indianCurrencyParser}
                    />
                    <small className="text-muted">
                      Available: ₹
                      {calculateRemainingAmount(
                        "mobilization"
                      ).toLocaleString("en-IN")}
                    </small>
                  </div>
                </Col>
              )}

              <Col lg="6">
                <div className="form-group">
                  <label className="form-label">Interest Charged</label>
                  <Switch
                    checked={
                      paymentConditionsWatch?.mobilizationAdvance
                        ?.interestCharged
                    }
                    onChange={(checked) =>
                      updatePaymentCondition(
                        "mobilizationAdvance",
                        "interestCharged",
                        checked
                      )
                    }
                  />
                </div>
              </Col>

              {paymentConditionsWatch?.mobilizationAdvance?.interestCharged && (
                <Col lg="6">
                  <div className="form-group">
                    <label className="form-label">Interest Rate (%)</label>
                    <InputNumber
                      min={0}
                      max={100}
                      value={
                        paymentConditionsWatch?.mobilizationAdvance
                          ?.interestRate
                      }
                      onChange={(value) =>
                        updatePaymentCondition(
                          "mobilizationAdvance",
                          "interestRate",
                          value
                        )
                      }
                      style={{ width: "100%" }}
                      placeholder="Enter interest rate"
                    />
                  </div>
                </Col>
              )}

              <Col lg="6">
                <FormSelect
                  name="paymentConditions.mobilizationAdvance.recoveryType"
                  label="Recovery Type"
                  options={recoveryTypeOptions}
                  defaultValue={
                    paymentConditionsWatch?.mobilizationAdvance?.recoveryType
                  }
                  onChange={(value) =>
                    updatePaymentCondition(
                      "mobilizationAdvance",
                      "recoveryType",
                      value
                    )
                  }
                  placeholder="Select recovery type"
                  required={false}
                />
              </Col>

              <Col lg="6">
                <div className="form-group">
                  <label className="form-label">Recovery % (per RA Bill)</label>
                  <InputNumber
                    min={0}
                    max={100}
                    value={
                      paymentConditionsWatch?.mobilizationAdvance
                        ?.recoveryPercentRate
                    }
                    onChange={(value) =>
                      updatePaymentCondition(
                        "mobilizationAdvance",
                        "recoveryPercentRate",
                        value
                      )
                    }
                    style={{ width: "100%" }}
                    placeholder="Enter recovery percentage"
                  />
                  <small className="text-muted">
                    Percentage of mobilization advance to recover in each RA
                    Bill
                  </small>
                </div>
              </Col>

              <Col lg="6">
                <div className="form-group">
                  <label className="form-label">
                    Recovery Upto % of Work Order
                  </label>
                  <InputNumber
                    min={0}
                    max={100}
                    value={
                      paymentConditionsWatch?.mobilizationAdvance
                        ?.recoveryCapPercent
                    }
                    onChange={(value) =>
                      updatePaymentCondition(
                        "mobilizationAdvance",
                        "recoveryCapPercent",
                        value
                      )
                    }
                    style={{ width: "100%" }}
                    placeholder="Enter upto % of work order value"
                  />
                  <small className="text-muted">
                    Recovery must be completed before reaching this % of work
                    order value
                  </small>
                </div>
              </Col>

              <Col lg="12">
                <div className="alert alert-success">
                  <DollarOutlined className="me-2" />
                  <strong>
                    Calculated Mobilization Amount: ₹
                    {calculateMobilizationAmount().toLocaleString("en-IN")}
                  </strong>
                  {paymentConditionsWatch?.mobilizationAdvance
                    ?.recoveryPercentRate &&
                    paymentConditionsWatch?.mobilizationAdvance
                      ?.recoveryPercentRate > 0 && (
                      <>
                        <br />
                        <small className="d-block mt-1">
                          Recovery per RA Bill:{" "}
                          {
                            paymentConditionsWatch.mobilizationAdvance
                              .recoveryPercentRate
                          }
                          % of each RA Bill
                          {paymentConditionsWatch?.mobilizationAdvance
                            ?.recoveryCapPercent &&
                            paymentConditionsWatch.mobilizationAdvance
                              .recoveryCapPercent > 0 && (
                              <>
                                {" "}
                                upto{" "}
                                {
                                  paymentConditionsWatch.mobilizationAdvance
                                    .recoveryCapPercent
                                }
                                % of the value of work order
                              </>
                            )}
                        </small>
                      </>
                    )}
                </div>
              </Col>
            </Row>
          </Card>

          {/* 2. Retention Amount */}
          <Card
            size="small"
            title={
              <div className="d-flex justify-content-between align-items-center">
                <span>2. Retention Amount</span>
                {/* For OPEN work type, don't show "No Balance Available" warning */}
                {!isOpenWorkType &&
                  calculateRemainingAmount("retention") <= 0 && (
                    <span className="badge bg-warning text-dark">
                      No Balance Available
                    </span>
                  )}
              </div>
            }
            className="mb-4"
            style={{
              // For OPEN work type, always keep enabled even when contract value = 0
              opacity:
                !isOpenWorkType && calculateRemainingAmount("retention") <= 0
                  ? 0.6
                  : 1,
              pointerEvents:
                !isOpenWorkType && calculateRemainingAmount("retention") <= 0
                  ? "none"
                  : "auto",
            }}
          >
            {/* For OPEN work type, don't show balance warning */}
            {!isOpenWorkType && calculateRemainingAmount("retention") <= 0 && (
              <div className="alert alert-warning mb-3">
                <InfoCircleOutlined className="me-2" />
                No remaining balance available for retention amount. Please
                reduce other payment conditions.
              </div>
            )}
            <Row className="gy-3">
              <Col lg="12">
                <div className="form-group">
                  <label className="form-label">Amount Type</label>
                  <div className="text-muted small">
                    Percentage of RA Bill (Fixed)
                  </div>
                </div>
              </Col>

              <Col lg="6">
                <div className="form-group">
                  <label className="form-label">
                    Percentage of RA Bill (%)
                  </label>
                  <InputNumber
                    min={0}
                    max={50}
                    value={
                      paymentConditionsWatch?.retentionAmount
                        ?.percentageOfRABill
                    }
                    onChange={(value) => {
                      // For OPEN work type, skip balance validation as contract value is 0
                      if (!isOpenWorkType) {
                        const calculatedAmount = value
                          ? (estimatedContractValue * value) / 100
                          : 0;
                        const remainingAmount =
                          calculateRemainingAmount("retention");

                        if (calculatedAmount > remainingAmount) {
                          Swal.fire({
                            icon: "warning",
                            title: "Insufficient Balance",
                            text: `Total payment conditions cannot exceed contract value. Available: ₹${remainingAmount.toLocaleString("en-IN")}`,
                          });
                          return;
                        }
                      }
                      updatePaymentCondition(
                        "retentionAmount",
                        "percentageOfRABill",
                        value
                      );
                      // Ensure isPercentOfRABill is set to true (percentage mode)
                      updatePaymentCondition(
                        "retentionAmount",
                        "isPercentOfRABill",
                        true
                      );
                    }}
                    style={{ width: "100%" }}
                    placeholder="Enter percentage"
                  />
                  {/* For OPEN work type, don't show available balance */}
                  {!isOpenWorkType && (
                    <small className="text-muted">
                      Available: ₹
                      {calculateRemainingAmount("retention").toLocaleString("en-IN")}
                    </small>
                  )}
                </div>
              </Col>

              <Col lg="12">
                <Row className="gy-2">
                  <Col lg="4">
                    <div className="form-group">
                      <label className="form-label">
                        DLP (Defect Liability Period)
                      </label>
                      <InputNumber
                        min={1}
                        value={
                          paymentConditionsWatch?.retentionAmount?.dlpAmount ||
                          1
                        }
                        onChange={(value) =>
                          updateRetentionDLP("dlpAmount", value)
                        }
                        style={{ width: "100%" }}
                        placeholder="Enter DLP amount"
                      />
                    </div>
                  </Col>
                  <Col lg="4">
                    <div className="form-group">
                      <label className="form-label">DLP Unit</label>
                      <Select
                        value={
                          paymentConditionsWatch?.retentionAmount?.dlpUnit ||
                          "years"
                        }
                        onChange={(value) =>
                          updateRetentionDLP("dlpUnit", value)
                        }
                        style={{ width: "100%" }}
                        options={dlpUnitOptions}
                        placeholder="Select unit"
                      />
                    </div>
                  </Col>
                  <Col lg="4">
                    <div className="form-group">
                      <label className="form-label">
                        Release Date (Auto-calculated)
                      </label>
                      <DatePicker
                        value={
                          paymentConditionsWatch?.retentionAmount?.releaseDate
                            ? dayjs(
                                paymentConditionsWatch.retentionAmount
                                  .releaseDate
                              )
                            : null
                        }
                        onChange={(date) =>
                          updatePaymentCondition(
                            "retentionAmount",
                            "releaseDate",
                            date?.format("YYYY-MM-DD")
                          )
                        }
                        style={{ width: "100%" }}
                        placeholder="Auto-calculated from DLP"
                        disabled={true}
                      />
                    </div>
                  </Col>
                </Row>
              </Col>

              <Col lg="12">
                <div className="alert alert-success">
                  <DollarOutlined className="me-2" />
                  <strong>
                    Estimated Retention Amount: ₹
                    {calculateRetentionAmount().toLocaleString("en-IN")}
                  </strong>
                  {paymentConditionsWatch?.retentionAmount?.dlpAmount &&
                    paymentConditionsWatch?.retentionAmount?.dlpUnit && <br />}
                  {paymentConditionsWatch?.retentionAmount?.dlpAmount &&
                    paymentConditionsWatch?.retentionAmount?.dlpUnit && (
                      <small className="d-block mt-1">
                        DLP: {paymentConditionsWatch.retentionAmount.dlpAmount}{" "}
                        {paymentConditionsWatch.retentionAmount.dlpUnit}
                        (Release Date:{" "}
                        {paymentConditionsWatch?.retentionAmount?.releaseDate
                          ? dayjs(
                              paymentConditionsWatch.retentionAmount.releaseDate
                            ).format("DD/MM/YYYY")
                          : "Not set"}
                        )
                      </small>
                    )}
                </div>
              </Col>
            </Row>
          </Card>

          {/* 3. On Hold Amount */}
          <Card
            size="small"
            title={
              <div className="d-flex justify-content-between align-items-center">
                <span>3. On Hold Amount</span>
                {/* For OPEN work type, don't show "No Balance Available" warning */}
                {!isOpenWorkType && calculateRemainingAmount("onHold") <= 0 && (
                  <span className="badge bg-warning text-dark">
                    No Balance Available
                  </span>
                )}
              </div>
            }
            className="mb-4"
            style={{
              // For OPEN work type, always keep enabled even when contract value = 0
              opacity:
                !isOpenWorkType && calculateRemainingAmount("onHold") <= 0
                  ? 0.6
                  : 1,
              pointerEvents:
                !isOpenWorkType && calculateRemainingAmount("onHold") <= 0
                  ? "none"
                  : "auto",
            }}
          >
            {/* For OPEN work type, don't show balance warning */}
            {!isOpenWorkType && calculateRemainingAmount("onHold") <= 0 && (
              <div className="alert alert-warning mb-3">
                <InfoCircleOutlined className="me-2" />
                No remaining balance available for on hold amount. Please reduce
                other payment conditions.
              </div>
            )}
            <Row className="gy-3">
              <Col lg="12">
                <div className="form-group">
                  <label className="form-label">Amount Type</label>
                  <div className="text-muted small">
                    Percentage of Contract Value (Fixed)
                  </div>
                </div>
              </Col>

              <Col lg="6">
                <div className="form-group">
                  <label className="form-label">
                    Percentage of Contract Value (%)
                  </label>
                  <InputNumber
                    min={0}
                    max={100}
                    value={
                      paymentConditionsWatch?.onHoldAmount?.percentageOfContract
                    }
                    onChange={(value) => {
                      // For OPEN work type, skip balance validation as contract value is 0
                      if (!isOpenWorkType) {
                        const calculatedAmount = value
                          ? (estimatedContractValue * value) / 100
                          : 0;
                        const remainingAmount =
                          calculateRemainingAmount("onHold");

                        if (calculatedAmount > remainingAmount) {
                          Swal.fire({
                            icon: "warning",
                            title: "Insufficient Balance",
                            text: `Total payment conditions cannot exceed contract value. Available: ₹${remainingAmount.toLocaleString("en-IN")}`,
                          });
                          return;
                        }
                      }
                      updatePaymentCondition(
                        "onHoldAmount",
                        "percentageOfContract",
                        value
                      );
                      // Ensure isPercentOfContract is set to true (percentage mode)
                      updatePaymentCondition(
                        "onHoldAmount",
                        "isPercentOfContract",
                        true
                      );
                    }}
                    style={{ width: "100%" }}
                    placeholder="Enter percentage"
                  />
                  {/* For OPEN work type, don't show available balance */}
                  {!isOpenWorkType && (
                    <small className="text-muted">
                      Available: ₹
                      {calculateRemainingAmount("onHold").toLocaleString("en-IN")}
                    </small>
                  )}
                </div>
              </Col>

              <Col lg="12">
                <Row className="gy-2">
                  <Col lg="4">
                    <div className="form-group">
                      <label className="form-label">
                        DLP (Defect Liability Period)
                      </label>
                      <InputNumber
                        min={1}
                        value={
                          paymentConditionsWatch?.onHoldAmount?.dlpAmount || 1
                        }
                        onChange={(value) =>
                          updateOnHoldDLP("dlpAmount", value)
                        }
                        style={{ width: "100%" }}
                        placeholder="Enter DLP amount"
                      />
                    </div>
                  </Col>
                  <Col lg="4">
                    <div className="form-group">
                      <label className="form-label">DLP Unit</label>
                      <Select
                        value={
                          paymentConditionsWatch?.onHoldAmount?.dlpUnit ||
                          "years"
                        }
                        onChange={(value) => updateOnHoldDLP("dlpUnit", value)}
                        style={{ width: "100%" }}
                        options={dlpUnitOptions}
                        placeholder="Select unit"
                      />
                    </div>
                  </Col>
                  <Col lg="4">
                    <div className="form-group">
                      <label className="form-label">
                        Release Date (Auto-calculated)
                      </label>
                      <DatePicker
                        value={
                          paymentConditionsWatch?.onHoldAmount?.releaseDate
                            ? dayjs(
                                paymentConditionsWatch.onHoldAmount.releaseDate
                              )
                            : null
                        }
                        onChange={(date) =>
                          updatePaymentCondition(
                            "onHoldAmount",
                            "releaseDate",
                            date?.format("YYYY-MM-DD")
                          )
                        }
                        style={{ width: "100%" }}
                        placeholder="Auto-calculated from DLP"
                        disabled={true}
                      />
                    </div>
                  </Col>
                </Row>
              </Col>

              <Col lg="12">
                <div className="alert alert-success">
                  <DollarOutlined className="me-2" />
                  <strong>
                    Calculated On Hold Amount: ₹
                    {calculateOnHoldAmount().toLocaleString("en-IN")}
                  </strong>
                  {paymentConditionsWatch?.onHoldAmount?.dlpAmount &&
                    paymentConditionsWatch?.onHoldAmount?.dlpUnit && <br />}
                  {paymentConditionsWatch?.onHoldAmount?.dlpAmount &&
                    paymentConditionsWatch?.onHoldAmount?.dlpUnit && (
                      <small className="d-block mt-1">
                        DLP: {paymentConditionsWatch.onHoldAmount.dlpAmount}{" "}
                        {paymentConditionsWatch.onHoldAmount.dlpUnit}
                        (Release Date:{" "}
                        {paymentConditionsWatch?.onHoldAmount?.releaseDate
                          ? dayjs(
                              paymentConditionsWatch.onHoldAmount.releaseDate
                            ).format("DD/MM/YYYY")
                          : "Not set"}
                        )
                      </small>
                    )}
                </div>
              </Col>
            </Row>
          </Card>

          {/* 4. Pay on Work Completion */}
          <Card size="small" title="4. Pay on Work Completion" className="mb-4">
            <Row className="gy-3">
              <Col lg="12">
                <div className="form-group">
                  <label className="form-label">Payment Schedule</label>
                  <Radio.Group
                    value={paymentConditionsWatch?.payOnWorkCompletion}
                    onChange={(e) => {
                      console.log("Selected value:", e.target.value);
                      updatePaymentCondition(
                        "",
                        "payOnWorkCompletion",
                        e.target.value
                      );
                    }}
                  >
                    <div className="d-flex flex-column gap-2">
                      <Radio value={false}>
                        Pay based on RA Bills during execution
                      </Radio>
                      <Radio value={true}>Pay only after work completion</Radio>
                    </div>
                  </Radio.Group>
                  <div className="mt-4">
                    <small className="text-muted">
                      {paymentConditionsWatch?.payOnWorkCompletion
                        ? "⚠️ Final payment will be made only after complete work completion and acceptance"
                        : "💳 Payments will be processed based on RA bills submitted during project execution"}
                    </small>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Payment Conditions Summary */}
          {!isOpenWorkType && (
             <Card
            size="small"
            title="Payment Conditions Summary"
            className="mb-4"
          >
            <Row className="gy-3">
              <Col lg="12">
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <td className="fw-bold">Estimated Contract Value</td>
                        <td className="text-end">
                          ₹{estimatedContractValue.toLocaleString("en-IN")}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          Mobilization Advance
                          {paymentConditionsWatch?.mobilizationAdvance
                            ?.recoveryPercentRate &&
                            paymentConditionsWatch.mobilizationAdvance
                              .recoveryPercentRate > 0 && (
                              <div className="small text-muted">
                                Recovery:{" "}
                                {
                                  paymentConditionsWatch.mobilizationAdvance
                                    .recoveryPercentRate
                                }
                                % per RA Bill
                                {/* (₹{((calculateMobilizationAmount() * paymentConditionsWatch.mobilizationAdvance.recoveryPercentRate) / 100).toLocaleString("en-IN")}) */}
                              </div>
                            )}
                        </td>
                        <td className="text-end">
                          ₹{calculateMobilizationAmount().toLocaleString("en-IN")}
                        </td>
                      </tr>
                      <tr>
                        <td>Retention Amount (per RA Bill)</td>
                        <td className="text-end">
                          ₹{calculateRetentionAmount().toLocaleString("en-IN")}
                        </td>
                      </tr>
                      <tr>
                        <td>On Hold Amount (per RA Bill)</td>
                        <td className="text-end">
                          ₹{calculateOnHoldAmount().toLocaleString("en-IN")}
                        </td>
                      </tr>
                      {/* <tr className="table-active">
                        <td className="fw-bold">Total Payment Conditions</td>
                        <td className="text-end fw-bold">₹{calculateTotalPaymentConditions().toLocaleString("en-IN")}</td>
                      </tr> */}
                      {/* <tr className={calculateRemainingAmount() < 0 ? "table-danger" : "table-success"}>
                        <td className="fw-bold">
                          {calculateRemainingAmount() < 0 ? "⚠️ Exceeds Contract Value by" : "Remaining Balance"}
                        </td>
                        <td className="text-end fw-bold">
                          ₹{Math.abs(calculateRemainingAmount()).toLocaleString("en-IN")}
                        </td>
                      </tr> */}
                    </tbody>
                  </table>
                </div>
                {!isOpenWorkType && calculateRemainingAmount() < 0 && (
                  <div className="alert alert-danger">
                    <strong>Warning:</strong> Total payment conditions exceed
                    the contract value. Please adjust the amounts.
                  </div>
                )}
              </Col>
            </Row>
          </Card>
          )}
         

          {/* Payment Summary */}
          {/* <Card size="small" title="Payment Summary" className="mb-4">
            <Row className="gy-2">
              <Col lg="6">
                <div className="d-flex justify-content-between">
                  <span>Contract Value:</span>
                  <strong>₹{estimatedContractValue.toLocaleString("en-IN")}</strong>
                </div>
              </Col>
              <Col lg="6">
                <div className="d-flex justify-content-between">
                  <span>Mobilization Advance:</span>
                  <strong>₹{calculateMobilizationAmount().toLocaleString("en-IN")}</strong>
                </div>
              </Col>
              <Col lg="6">
                <div className="d-flex justify-content-between">
                  <span>Retention Amount:</span>
                  <strong>₹{calculateRetentionAmount().toLocaleString("en-IN")}</strong>
                </div>
              </Col>
              <Col lg="6">
                <div className="d-flex justify-content-between">
                  <span>On Hold Amount:</span>
                  <strong>₹{calculateOnHoldAmount().toLocaleString("en-IN")}</strong>
                </div>
              </Col>
              <Col lg="12">
                <Divider />
                <div className="d-flex justify-content-between">
                  <span><strong>Net Payable:</strong></span>
                  <strong className="text-success">
                    ₹{(estimatedContractValue - calculateRetentionAmount() - calculateOnHoldAmount()).toLocaleString("en-IN")}
                  </strong>
                </div>
              </Col>
            </Row>
          </Card> */}
        </Card>

        <div className="actions clearfix">
          <ul>
            <li>
              <Button color="primary" type="submit">
                Next
              </Button>
            </li>
            <li>
              <Button type="button" onClick={handlePrevious}>
                Previous
              </Button>
            </li>
          </ul>
        </div>
      </form>
    </>
  );
};

export default WorkOrderPaymentConditions;
